import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  BellAlertIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  ClipboardDocumentCheckIcon,
  DevicePhoneMobileIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  Squares2X2Icon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { api } from "../utils/api";
import { toastError, toastInfo, toastSuccess } from "../utils/toast";
import plannerLogo from "../assets/logo/Logo-Planner-Turni.png";

const features = [
  {
    title: "Pianificazione turni smart",
    description:
      "Crea calendari ordinati, assegna i volontari e gestisci turni speciali in pochi clic.",
    icon: CalendarDaysIcon,
  },
  {
    title: "Gestione volontari e ruoli",
    description:
      "Anagrafiche complete, ruoli personalizzati e deleghe per una gestione chiara.",
    icon: UserGroupIcon,
  },
  {
    title: "Disponibilità immediate",
    description:
      "Raccogli le disponibilità, monitora i cambi e risolvi i turni mancanti.",
    icon: ClipboardDocumentCheckIcon,
  },
  {
    title: "Notifiche e promemoria",
    description:
      "Avvisi push e comunicazioni rapide per mantenere il team sempre allineato.",
    icon: BellAlertIcon,
  },
  {
    title: "Report e cronologie",
    description:
      "Storico completo dei turni e statistiche per una visione chiara del servizio.",
    icon: ChartBarIcon,
  },
  {
    title: "Documenti e istruzioni",
    description:
      "Centralizza autorizzazioni, procedure e materiali per ogni servizio.",
    icon: DocumentTextIcon,
  },
  {
    title: "Multi-servizio modulare",
    description:
      "Postazioni, adunanze, pulizie, gruppi e registri: tutto nello stesso spazio.",
    icon: Squares2X2Icon,
  },
  {
    title: "Mobile e sicuro",
    description:
      "Accesso rapido da smartphone con controlli di sicurezza e privacy avanzati.",
    icon: ShieldCheckIcon,
  },
];

const RichiestaCongregazione = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    nome: "",
    cognome: "",
    congregazione: "",
    luogo: "",
    telefono: "",
    email: "",
    dettagli: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post("/richiesta-congregazione", {
        nome: formData.nome.trim(),
        cognome: formData.cognome.trim(),
        congregazione: formData.congregazione.trim(),
        luogo: formData.luogo.trim(),
        telefono: formData.telefono.trim(),
        email: formData.email.trim(),
        dettagli: formData.dettagli.trim(),
      });

      const wasSkipped = response?.status === 202 || response?.data?.skipped;

      if (wasSkipped) {
        toastInfo("Richiesta registrata: invio email non configurato.");
      } else {
        toastSuccess("Richiesta inviata con successo!");
      }

      setSubmitted(true);
      setFormData({
        nome: "",
        cognome: "",
        congregazione: "",
        luogo: "",
        telefono: "",
        email: "",
        dettagli: "",
      });
    } catch (error) {
      console.error("Errore invio richiesta congregazione:", error);
      toastError(
        error.response?.data?.details ||
          error.response?.data?.message ||
          "Impossibile inviare la richiesta. Riprova più tardi."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-blue-50" />
        <div className="absolute -top-24 right-[-10%] h-72 w-72 rounded-full bg-primary-100 blur-3xl opacity-70" />
        <div className="absolute bottom-[-120px] left-[-10%] h-80 w-80 rounded-full bg-blue-200 blur-3xl opacity-60" />

        <div className="relative max-w-6xl mx-auto px-6 py-16 lg:py-20 fade-in">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src={plannerLogo}
                alt="Planner Turni"
                className="h-12 w-12"
              />
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-primary-600">
                  Planner Turni
                </p>
                <p className="text-sm text-gray-500">
                  Gestione turni per congregazioni
                </p>
              </div>
            </div>
            <Link
              to="/login"
              className="hidden sm:inline-flex text-sm font-semibold text-primary-600 hover:text-primary-700"
            >
              Hai già un account? Accedi
            </Link>
          </div>

          <div className="mt-12 grid gap-12 lg:grid-cols-[1.1fr_0.9fr] items-center">
            <div className="space-y-6">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/70 px-4 py-1 text-xs font-semibold uppercase tracking-wide text-primary-700 shadow-sm">
                Nuova attivazione
                <DevicePhoneMobileIcon className="h-4 w-4" />
              </span>
              <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl leading-tight">
                Benvenuto! Attiva Planner Turni per la tua congregazione.
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed">
                Pianifica i turni, organizza i volontari e comunica in modo
                semplice con uno strumento elegante, professionale e ottimizzato
                per mobile.
              </p>
              <div className="flex flex-wrap gap-3">
                {[
                  "Configurazione guidata",
                  "Supporto dedicato",
                  "Attivazione rapida",
                ].map((label) => (
                  <span
                    key={label}
                    className="rounded-full border border-primary-200 bg-white/80 px-4 py-2 text-sm text-primary-700"
                  >
                    {label}
                  </span>
                ))}
              </div>
            </div>

            <div className="bg-white/90 backdrop-blur rounded-2xl border border-gray-200 shadow-xl p-6 space-y-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Cosa ottieni con Planner Turni
                </h2>
                <p className="text-sm text-gray-600 mt-2">
                  Una piattaforma completa per coordinare ogni attività della
                  congregazione con strumenti moderni e flessibili.
                </p>
              </div>
              <div className="grid gap-4">
                {[
                  {
                    label: "Configurazioni personalizzabili",
                    value: "Turni, servizi, ruoli e gruppi",
                  },
                  {
                    label: "Automazioni",
                    value: "Promemoria, disponibilità e report",
                  },
                  {
                    label: "Sicurezza",
                    value: "Accessi controllati e dati protetti",
                  },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="rounded-xl border border-gray-100 bg-white px-4 py-3 shadow-sm"
                  >
                    <p className="text-sm font-semibold text-gray-800">
                      {item.label}
                    </p>
                    <p className="text-sm text-gray-500">{item.value}</p>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between text-sm text-gray-500">
                <span>Tempo medio di attivazione</span>
                <span className="font-semibold text-gray-900">48 ore</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-12 fade-in">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <p className="text-sm uppercase tracking-[0.2em] text-primary-600">
              Funzionalità principali
            </p>
            <h2 className="mt-2 text-3xl font-bold text-gray-900">
              Una panoramica completa, pensata per il servizio.
            </h2>
          </div>
          <p className="text-base text-gray-600 max-w-xl">
            Planner Turni unisce tutti gli strumenti di cui hai bisogno in un
            ambiente unico: calendario, comunicazioni, report, documenti e
            gestione dei volontari.
          </p>
        </div>

        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {features.map((feature) => {
            const Icon = feature.icon;
            return (
              <div
                key={feature.title}
                className="group rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-lg"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="mt-4 text-base font-semibold text-gray-900">
                  {feature.title}
                </h3>
                <p className="mt-2 text-sm text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-16 fade-in">
        <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr] items-start">
          <div className="rounded-2xl bg-white shadow-xl border border-gray-200 p-8">
            <div className="flex items-start justify-between gap-6">
              <div>
                <p className="text-sm uppercase tracking-[0.2em] text-primary-600">
                  Richiesta attivazione
                </p>
                <h2 className="mt-2 text-2xl font-bold text-gray-900">
                  Richiedi il tuo tenant dedicato
                </h2>
                <p className="mt-2 text-sm text-gray-600">
                  Compila il modulo e ti ricontatteremo per definire la
                  configurazione migliore per la tua congregazione.
                </p>
              </div>
            </div>

            {submitted && (
              <div className="mt-6 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                Grazie! Abbiamo ricevuto la tua richiesta. Ti risponderemo al
                più presto.
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-6 space-y-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="form-label" htmlFor="nome">
                    Nome
                  </label>
                  <input
                    id="nome"
                    name="nome"
                    type="text"
                    value={formData.nome}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Mario"
                    required
                  />
                </div>
                <div>
                  <label className="form-label" htmlFor="cognome">
                    Cognome
                  </label>
                  <input
                    id="cognome"
                    name="cognome"
                    type="text"
                    value={formData.cognome}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Rossi"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="form-label" htmlFor="congregazione">
                  Congregazione richiedente
                </label>
                <input
                  id="congregazione"
                  name="congregazione"
                  type="text"
                  value={formData.congregazione}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Congregazione Centro"
                  required
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <div className="sm:col-span-1">
                  <label className="form-label" htmlFor="luogo">
                    Luogo
                  </label>
                  <input
                    id="luogo"
                    name="luogo"
                    type="text"
                    value={formData.luogo}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Roma"
                    required
                  />
                </div>
                <div className="sm:col-span-1">
                  <label className="form-label" htmlFor="telefono">
                    Telefono
                  </label>
                  <input
                    id="telefono"
                    name="telefono"
                    type="tel"
                    inputMode="tel"
                    value={formData.telefono}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="+39 333 123 4567"
                    required
                  />
                </div>
                <div className="sm:col-span-1">
                  <label className="form-label" htmlFor="email">
                    Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="email@congregazione.it"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="form-label" htmlFor="dettagli">
                  Dettagli
                </label>
                <textarea
                  id="dettagli"
                  name="dettagli"
                  rows={5}
                  value={formData.dettagli}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Inserisci eventuali note su dimensioni della congregazione, servizi gestiti o esigenze particolari."
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-md bg-primary-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition-colors duration-200 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? "Invio in corso..." : "Invia richiesta"}
              </button>
            </form>
          </div>

          <aside className="space-y-6">
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900">
                Come funziona
              </h3>
              <ul className="mt-4 space-y-3 text-sm text-gray-600">
                {[
                  "Riceviamo la richiesta e confermiamo i dati.",
                  "Prepariamo il tenant dedicato e la struttura iniziale.",
                  "Ti guidiamo nella configurazione dei servizi.",
                ].map((step) => (
                  <li key={step} className="flex items-start gap-3">
                    <span className="mt-1 h-2 w-2 rounded-full bg-primary-600" />
                    <span>{step}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-primary-200 bg-primary-50 p-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Hai bisogno di chiarimenti?
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                Scrivici pure indicando eventuali domande nel campo "Dettagli":
                il nostro team ti risponderà con tutte le informazioni.
              </p>
              <Link
                to="/login"
                className="mt-4 inline-flex items-center text-sm font-semibold text-primary-700 hover:text-primary-800"
              >
                Torna al login
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default RichiestaCongregazione;
